import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  BarChart3, 
  FileText, 
  User, 
  BookOpen, 
  Users, 
  ArrowRightLeft, 
  Eye, 
  Heart,
  LogOut,
  Settings,
  Menu,
  Bot,
  Moon,
  Zap
} from 'lucide-react';
import SabiwabeeLogo from './SabiwabeeLogo';
import MobileNav from './MobileNav';
import AIAssistant from './AIAssistant';
import EmailVerificationBanner from './EmailVerificationBanner';

const Layout = () => {
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  // Feature flags - set to false to hide features temporarily
  const SHOW_GROUP_THERAPY = false;

  const navigationItems = [
    { path: '/app', label: 'Home', icon: Heart },
    { path: '/app/dashboard', label: 'Progress', icon: BarChart3 },
    { path: '/app/assessments', label: 'Assessments', icon: FileText },
    { path: '/app/meditation', label: 'Meditation', icon: User },
    { path: '/app/journal', label: 'Journal', icon: BookOpen },
    { path: '/app/dream', label: 'Dream Companion', icon: Moon },
    { path: '/app/peer-support', label: 'Peer Support', icon: Users },
    ...(SHOW_GROUP_THERAPY ? [{ path: '/app/group-therapy', label: 'Group Therapy', icon: Users }] : []),
    // { path: '/app/shiller', label: 'Shiller Contest', icon: Zap }, // Commented out - can be used later
    { path: '/app/therapist-handover', label: 'Therapist Handover', icon: ArrowRightLeft },
    { path: '/app/transparency', label: 'AI Transparency', icon: Eye },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2 flex-shrink-0 mr-4">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                <SabiwabeeLogo className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold text-gray-900">Sabiwabee</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-1 overflow-x-auto scrollbar-hide flex-1 justify-center">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`nav-tab flex-shrink-0 ${isActive ? 'active' : ''}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              title="Menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* User Profile */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => navigate('/app/profile')}
                className="flex items-center space-x-1.5 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="Profile Settings"
              >
                <User className="w-4 h-4" />
                <span className="text-xs font-medium text-gray-900 hidden lg:block">{user?.name}</span>
                <Settings className="w-3 h-3" />
              </button>
              <button
                onClick={() => setShowAIAssistant(true)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="AI Assistant"
              >
                <Bot className="w-4 h-4" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-40">
          <div className="px-4 py-2 space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setShowMobileMenu(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors duration-200 ${
                    isActive 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
            <div className="border-t border-gray-200 pt-2 mt-2">
              <button
                onClick={() => {
                  navigate('/app/profile');
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors duration-200"
              >
                <User className="w-4 h-4" />
                <span className="font-medium">Profile</span>
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
        {/* Email verification banner disabled (COMMENTED OUT) */}
        {/* <EmailVerificationBanner /> */}
        <Outlet />
      </main>

      {/* Disclaimer Footer - Hide on group therapy pages */}
      {!location.pathname.includes('/group-therapy') && (
        <footer className="bg-white border-t border-gray-100 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Important Disclaimer:</strong> Sabiwabee is not a substitute for professional therapy. 
                For emergencies, call 988 (US) or your local crisis hotline. 
                If you're experiencing thoughts of self-harm or suicide, please seek immediate professional help.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                © 2024 Sabiwabee. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      )}

      {/* Mobile Navigation */}
      <MobileNav />
        
      {/* AI Assistant */}
      <AIAssistant isOpen={showAIAssistant} onClose={() => setShowAIAssistant(false)} />
    </div>
  );
};

export default Layout; 